import {TranslationObject} from '@colonist/i18n'
import {LobbyImage} from '@colonist/types'
import {MathUtils} from '../../../../../shared/utils/MathUtils'
import {createDivIn, createHeaderIn, createImgIn, createSliderIn, createSpanIn} from '../../ui_utils/DomUtils'
import {createTooltipIn} from '../../ui_utils/TooltipUtils.ts'

export class UIRoomSliderController {
    protected readonly container: HTMLDivElement
    protected sliderContainer: HTMLDivElement
    protected slider: HTMLInputElement
    private readonly onChange?: ((value: number) => void) | undefined
    protected onInput: (value: number) => void
    protected valueDisplay: HTMLSpanElement
    private readonly textList: string[] | undefined
    private readonly min: number
    max: number
    protected headerContainer: HTMLDivElement
    protected tickContainer: HTMLDivElement
    protected readonly title: TranslationObject
    protected readonly initialValue: number
    private readonly step: number

    private _isDisabled: boolean

    protected sliderWarningImage: HTMLImageElement
    private sliderWarningThreshold: number

    get isDisabled() {
        return this._isDisabled
    }
    set isDisabled(value: boolean) {
        this._isDisabled = value
        this.slider.disabled = value
        this.setSliderVisibility()
    }

    constructor(container: HTMLDivElement, title: TranslationObject, initialValue: number, min: number, max: number, step: number = 1, onChange?: (value: number) => void, textList?: string[], isDisabled: boolean = false) {
        if(textList && textList.length !== max - min + 1) throw new Error('Text list length must be equal to slider range')

        this.container = container
        this.title = title
        this.initialValue = initialValue
        this.min = min
        this.max = max
        this.step = step
        this.onChange = onChange
        this.textList = textList

        this.createSliderContainer()
        this.createHeader()
        this.createSlider()

        this.isDisabled = isDisabled
    }

    private createSliderContainer() {
        this.sliderContainer = createDivIn(this.container, 'room-settings-slider-container')
    }

    private createHeader() {
        this.headerContainer = createDivIn(this.sliderContainer, 'room-settings-slider-header')
        const titleContainer = createDivIn(this.headerContainer, 'room-settings-slider-title')
        createHeaderIn(titleContainer, this.title, '')
        this.sliderWarningImage = createImgIn(titleContainer, LobbyImage.DangerIcon, 'hidden')
        createTooltipIn(this.sliderWarningImage, {key: 'strings:roomPage.options.vpToWin.aboveRecommendedLimit'})

        this.valueDisplay = createSpanIn(this.headerContainer, '', {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.initialValue.toString()}})
    }

    private createSlider() {
        this.slider = createSliderIn(this.sliderContainer, 'room-settings-slider room-settings-slider-green-dynamic-background', '', this.min, this.max, this.step, this.initialValue)
        this.tickContainer = createDivIn(this.sliderContainer, 'room-settings-tick-container')

        for(let tickNumber = this.min; tickNumber <= this.max; tickNumber += this.step) {
            createDivIn(this.tickContainer, 'room-settings-tick')
        }

        this.setSliderProgressBackground()
        this.slider.oninput = () => this.handleOnInput()
        this.slider.onchange = () => this.handleOnChange()
        this.setSliderPointerEvents()
    }

    private removeSlider() {
        this.slider.remove()
        this.tickContainer.remove()
    }

    private setSliderVisibility() {
        const sliderElements = document.querySelectorAll('.room-settings-slider')
        const tickElements = document.querySelectorAll('.room-settings-tick-container')

        sliderElements.forEach((element: HTMLInputElement) => {
            element.style.display = this.isDisabled ? 'none' : ''
        })

        tickElements.forEach((element: HTMLInputElement) => {
            element.style.display = this.isDisabled ? 'none' : ''
        })
    }

    private setSliderPointerEvents() {
        this.slider.onpointerdown = () => {
            this.container.style.overflow = 'hidden'
        }

        this.slider.onpointercancel = () => {
            this.container.style.overflow = ''
        }
    }

    private setSliderProgressBackground() {
        this.isAboveWarningThreshold() ? this.sliderWarningImage.classList.remove('hidden') : this.sliderWarningImage.classList.add('hidden')

        const valuePercent = MathUtils.calculatePercentage(this.slider.valueAsNumber, this.min, this.max)
        if(this.textList != undefined) {
            const textListIndex = this.slider.valueAsNumber - this.min
            this.valueDisplay.innerText = this.textList[textListIndex]
        } else {
            this.valueDisplay.innerText = this.slider.value
        }

        const nonWarningValuePercent = this.calculateWarningValuePercent()

        this.slider.classList.toggle('room-settings-slider-green-red-dynamic-background', this.isAboveWarningThreshold())

        this.slider.style.setProperty('--nonWarningValuePercent', `${nonWarningValuePercent}%`)
        this.slider.style.setProperty('--valuePercent', `${valuePercent}%`)
    }

    private calculateWarningValuePercent() {
        if(!this.isAboveWarningThreshold()) return 0

        const thresholdTicker = this.getNthTickElement(this.sliderWarningThreshold)
        if(!thresholdTicker) return

        const thresholdTickerLocation = thresholdTicker.getBoundingClientRect().left
        const sliderLocation = this.slider.getBoundingClientRect().left

        const pixelDifference = thresholdTickerLocation - sliderLocation
        const sliderWidth = this.slider.getBoundingClientRect().width

        return MathUtils.calculatePercentage(pixelDifference, 1, sliderWidth)
    }

    private getNthTickElement(n: number): HTMLDivElement | undefined {
        return this.tickContainer.children.item(n - this.min) as HTMLDivElement
    }

    updateSliderTicks(max: number) {
        this.removeSlider()
        this.max = max
        this.createSlider()
    }

    setWarningThreshold(warningThreshold: number) {
        this.sliderWarningThreshold = warningThreshold
    }

    private isAboveWarningThreshold() {
        return this.slider.valueAsNumber > this.sliderWarningThreshold
    }

    private handleOnInput() {
        if(this.isDisabled) return
        if(this.onInput != undefined) {
            this.onInput(this.slider.valueAsNumber)
        }
        this.setSliderProgressBackground()
    }

    private handleOnChange() {
        this.container.style.overflow = ''
        if(this.isDisabled) return
        if(this.onChange) this.onChange(this.slider.valueAsNumber)
    }

    setOnInput(onInput: (value: number) => void) {
        this.onInput = onInput
    }

    setValue(value: number, isDisabled: boolean = false) {
        this.slider.value = value.toString()
        this.isDisabled = isDisabled
        this.setSliderProgressBackground()
    }
}
